import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Send, Save, Loader2, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { registerPushNotifications, unsubscribePush, isPushSupported, getPushPermission } from '../utils/pushNotifications';
import { API } from '../config/apiBase';


export default function NotificationPreferences({ token, compact = false }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: 'default' });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [allCats, setAllCats] = useState([]);

  const toggleCat = (slug) => {
    setPrefs(p => {
      const cur = Array.isArray(p.categories) ? p.categories : [];
      const next = cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug];
      return { ...p, categories: next };
    });
  };

  const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const loadPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/push/preferences`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPrefs(d.preferences); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/push/history`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setHistory(d.notifications || []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    setPushStatus({
      supported: isPushSupported(),
      permission: getPushPermission(),
    });
    fetch(`${API}/categories`).then(r => r.json()).then(d => setAllCats(Array.isArray(d) ? d : [])).catch(() => {});
    Promise.all([loadPrefs(), loadHistory()]).finally(() => setLoading(false));
  }, []);

  const handleEnablePush = async () => {
    const result = await registerPushNotifications(token);
    setPushStatus({ supported: result.supported, permission: result.permission || getPushPermission() });
    if (result.subscribed) {
      toast.success('Notificacoes push ativadas!');
      setPrefs(p => ({ ...p, web_push: true }));
      await savePrefs({ ...prefs, web_push: true });
    } else if (result.permission === 'denied') {
      toast.error('Permissao negada. Libere nas configuracoes do navegador.');
    }
  };

  const handleDisablePush = async () => {
    await unsubscribePush(token);
    setPushStatus(p => ({ ...p, permission: 'default' }));
    setPrefs(p => ({ ...p, web_push: false }));
    await savePrefs({ ...prefs, web_push: false });
    toast.success('Notificacoes push desativadas');
  };

  const savePrefs = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/push/preferences`, {
        method: 'PUT', headers, body: JSON.stringify(data || prefs),
      });
      if (res.ok) toast.success('Preferencias salvas');
      else toast.error('Erro ao salvar');
    } catch (e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin" style={{ color: 'var(--sv-accent)' }} /></div>;

  if (!prefs) return null;

  const ToggleRow = ({ label, desc, checked, onChange, testId }) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="text-xs" style={{ color: '#ccc' }}>{label}</div>
        {desc && <div className="text-[10px]" style={{ color: '#555' }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)} className="relative w-10 h-5 rounded-full transition-colors"
        style={{ background: checked ? 'var(--sv-accent)' : 'var(--surface-3)' }} data-testid={testId}>
        <span className="absolute top-0.5 rounded-full w-4 h-4 bg-white transition-all"
          style={{ left: checked ? '22px' : '2px' }} />
      </button>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="notification-preferences">
      {/* Push Status Card */}
      <div className="rounded-lg p-4" style={{ background: '#121212', border: '1px solid rgba(125,165,255,0.16)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Bell size={14} style={{ color: 'var(--sv-accent)' }} />
          <span className="text-sm font-medium" style={{ color: '#fff' }}>Notificacoes Push</span>
          {pushStatus.permission === 'granted' && prefs.web_push && (
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#0a1a0a', color: '#10B981' }}>Ativo</span>
          )}
        </div>

        {!pushStatus.supported ? (
          <div className="flex items-center gap-2 text-xs" style={{ color: '#888' }}>
            <AlertTriangle size={12} style={{ color: '#F59E0B' }} />
            Navegador nao suporta notificacoes push
          </div>
        ) : pushStatus.permission === 'granted' && prefs.web_push ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs" style={{ color: '#10B981' }}>
              <Check size={12} /> Notificacoes ativas neste dispositivo
            </div>
            <button onClick={handleDisablePush} className="text-[10px] px-2 py-1 rounded" style={{ color: '#EF4444', background: '#1a0a0a' }} data-testid="disable-push-btn">
              <BellOff size={10} className="inline mr-1" /> Desativar
            </button>
          </div>
        ) : (
          <button onClick={handleEnablePush}
            className="px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="enable-push-btn">
            <Bell size={14} /> Ativar notificacoes push
          </button>
        )}
      </div>

      {/* Categorias de interesse */}
      <div className="rounded-lg p-4" style={{ background: '#121212', border: '1px solid rgba(125,165,255,0.16)' }}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium" style={{ color: '#fff' }}>Categorias de interesse</span>
        </div>
        <p className="text-[10px] mb-3" style={{ color: '#666' }}>
          Receba notificacoes e veja promocoes so das categorias escolhidas. Vazio = todas.
        </p>
        <div className="flex flex-wrap gap-2">
          {allCats.map(c => {
            const on = (prefs.categories || []).includes(c.slug);
            return (
              <button
                key={c.category_id || c.slug}
                onClick={() => toggleCat(c.slug)}
                className="text-xs px-2.5 py-1 rounded-full transition-colors"
                style={on
                  ? { background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)', fontWeight: 600 }
                  : { background: '#071A3A', color: '#bbb', border: '1px solid rgba(125,165,255,0.16)' }}
                data-testid={`notif-cat-${c.slug}`}
              >
                {on ? '✓ ' : ''}{c.name}
              </button>
            );
          })}
          {allCats.length === 0 && <span className="text-[10px]" style={{ color: '#555' }}>Carregando categorias...</span>}
        </div>
      </div>

      {/* Telegram Fallback */}
      <div className="rounded-lg p-4" style={{ background: '#121212', border: '1px solid rgba(125,165,255,0.16)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Send size={14} style={{ color: 'var(--sv-accent)' }} />
          <span className="text-sm font-medium" style={{ color: '#fff' }}>Telegram (fallback)</span>
        </div>
        <ToggleRow label="Receber via Telegram" desc="Notifica quando push nao disponivel"
          checked={!!prefs.telegram} onChange={v => setPrefs(p => ({ ...p, telegram: v }))}
          testId="toggle-telegram-notif" />
        {prefs.telegram && (
          <div className="mt-2">
            <label className="text-[10px] block mb-1" style={{ color: '#666' }}>Seu Chat ID</label>
            <input type="text" value={prefs.telegram_chat_id || ''} onChange={e => setPrefs(p => ({ ...p, telegram_chat_id: e.target.value }))}
              placeholder="Use @userinfobot para descobrir" className="w-full text-xs py-1.5 px-2 rounded"
              style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#fff' }} data-testid="input-telegram-chatid" />
          </div>
        )}
      </div>

      {/* Filters */}
      {!compact && (
        <div className="rounded-lg p-4" style={{ background: '#121212', border: '1px solid rgba(125,165,255,0.16)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium" style={{ color: '#fff' }}>Filtros</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] block mb-1" style={{ color: '#666' }}>Score minimo para notificar</label>
              <div className="flex items-center gap-2">
                <input type="range" min="50" max="100" step="5" value={prefs.min_score || 80}
                  onChange={e => setPrefs(p => ({ ...p, min_score: parseInt(e.target.value) }))}
                  className="flex-1" style={{ accentColor: 'var(--sv-accent)' }} data-testid="range-min-score" />
                <span className="text-xs font-bold w-8 text-right" style={{ color: 'var(--sv-accent)' }}>{prefs.min_score || 80}</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] block mb-1" style={{ color: '#666' }}>Desconto minimo (%)</label>
              <div className="flex items-center gap-2">
                <input type="range" min="0" max="80" step="5" value={prefs.min_discount || 30}
                  onChange={e => setPrefs(p => ({ ...p, min_discount: parseInt(e.target.value) }))}
                  className="flex-1" style={{ accentColor: 'var(--sv-accent)' }} data-testid="range-min-discount" />
                <span className="text-xs font-bold w-8 text-right" style={{ color: 'var(--sv-accent)' }}>{prefs.min_discount || 30}%</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] block mb-1" style={{ color: '#666' }}>Horario silencioso (opcional)</label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" max="23" value={prefs.quiet_hours_start ?? ''} onChange={e => setPrefs(p => ({ ...p, quiet_hours_start: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="De" className="w-16 text-xs py-1 px-2 rounded text-center"
                  style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#fff' }} data-testid="input-quiet-start" />
                <span className="text-[10px]" style={{ color: '#555' }}>ate</span>
                <input type="number" min="0" max="23" value={prefs.quiet_hours_end ?? ''} onChange={e => setPrefs(p => ({ ...p, quiet_hours_end: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="Ate" className="w-16 text-xs py-1 px-2 rounded text-center"
                  style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#fff' }} data-testid="input-quiet-end" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save */}
      <button onClick={() => savePrefs()} disabled={saving}
        className="w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90"
        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-notif-prefs">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar preferencias
      </button>

      {/* History */}
      {!compact && history.length > 0 && (
        <div>
          <button onClick={() => setShowHistory(!showHistory)} className="text-xs flex items-center gap-1" style={{ color: 'var(--sv-accent)' }}>
            {showHistory ? 'Ocultar' : 'Ver'} historico ({history.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
              {history.map((n, i) => (
                <div key={i} className="rounded p-2 text-[10px]" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)' }}>
                  <div className="font-medium" style={{ color: '#fff' }}>{n.title}</div>
                  <div style={{ color: '#888' }}>{n.body}</div>
                  <div style={{ color: '#444' }}>{n.sent_at ? new Date(n.sent_at).toLocaleString('pt-BR') : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
